import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon, Flex, Button, Box, Text, styled, Badge, Spinner } from '@goodrx/matisse';
import { debounce, capitalize } from 'lodash';
import { Drawer } from 'app/components/Drawer';
import { SearchResponse } from 'app/types/Infermedica';
import { search, suggest } from 'app/lib/infermedica';
import { useToggledState } from 'app/hooks';
import { SearchInput, SearchInputRefAttributes } from 'app/components/core';
import { CdnImage } from 'app/components/CdnImage';
import { List } from 'app/components/core/list';
import { Metric } from 'app/components/Metric';
import { trackEvent, onTriageEmptyResults } from 'app/lib/analytics';
import { useSynapse } from 'app/lib/synaptik';
import { TriageBlockConfig, TriageBlockChildProps } from '../TriageBlock';
import { SenderChatBubble, SavedResponseWrapper, createDiagnosisPayload } from '../shared';

const SymptomListItem = styled(Flex)(
  (p) => `
    padding: ${p.theme.space[3]}px;
    cursor: pointer;
    outline: none;
    align-items: center;

    &:focus, &:hover {
      background: ${p.theme.colors.greyLightest};
    }
  `
);

const SymptomBadge = styled(Badge).attrs((props) => ({
  ...props,
  variant: 'grey',
  role: 'button',
  tabIndex: 0,
}))(
  ({ theme: { colors, space } }) => `
    display: flex;
    align-items: center;
    margin-right: ${space[2]}px;
    margin-bottom: ${space[2]}px;
    border-radius: ${space[3]}px;
    background: white;
    border: 1px solid ${colors.greyLight};
    cursor: pointer;
    outline: none;
    font-size: 14px;
    color: ${colors.greyDarkest};
    transition: 350ms all;

    &:focus {
      background: ${colors.greyLightest};
    }

    &:hover {
      background: ${colors.greyLighter};
    }
  `
);

const ButtonContainer = styled(Box)(
  ({ theme: { colors, space } }) => `
    position: relative;
    background-color: white;
    padding: ${space[3]}px;
    border-top: 1px solid ${colors.grey};
  `
);

type ChiefComplaintData = SearchResponse[];

export const code = 'chiefComplaint';

export const Message: TriageBlockConfig['Message'] = () => {
  const firstName = useSynapse(({ user }) => user.state.profile?.firstName);

  return (
    <Flex flexDirection="column">
      <SenderChatBubble>
        Hi {firstName}, welcome to HeyDoctor!{' '}
        <span role="img" aria-label="Wave Emoji">
          👋
        </span>
      </SenderChatBubble>
      <SenderChatBubble>
        I&apos;m going to ask you some questions so that I can connect you with a HeyDoctor healthcare provider if
        appropriate.
      </SenderChatBubble>
      <SenderChatBubble>What symptoms are you experiencing?</SenderChatBubble>
    </Flex>
  );
};

const SymptomBadgeList: React.FC<{ symptoms: SearchResponse[]; onSymptomClick: (symptom: SearchResponse) => void }> = ({
  symptoms = [],
  onSymptomClick,
}) => (
  <Flex mt={2} flexWrap="wrap" mb={-2}>
    {symptoms.map((symptom) => (
      <SymptomBadge
        data-qa="symptom-badge"
        key={symptom.id}
        onClick={() => onSymptomClick(symptom)}
        onKeyPress={() => onSymptomClick(symptom)}>
        {symptom.label}
        <Icon name="close" style={{ marginLeft: 4, fontSize: 16 }} />
      </SymptomBadge>
    ))}
  </Flex>
);

const SearchHeaderContainer = styled(Flex)(
  ({ theme: { colors } }) => `
    flex-direction: column;
    border-bottom: 1px solid ${colors.grey};
  `
);

const SymptomSearchHeader: React.FC<{
  selectedSymptoms: SearchResponse[];
  searchTerm: string;
  onCloseDrawer: () => void;
  onSymptomBadgeClick: (symptom: SearchResponse) => void;
  onSearchTermChange: (term: string) => void;
  searchRef: React.MutableRefObject<SearchInputRefAttributes | null>;
}> = ({ selectedSymptoms, searchTerm, onSearchTermChange, onSymptomBadgeClick, onCloseDrawer, searchRef }) => (
  <SearchHeaderContainer p={3}>
    <Flex>
      <SearchInput
        ref={searchRef}
        onChange={(_: never, term: string) => onSearchTermChange(term)}
        onClear={() => onSearchTermChange('')}
        name="search"
        value={searchTerm}
        tabIndex={1}
        data-qa="symptom-search-input"
      />

      <Button variant="greyText" onClick={onCloseDrawer} tabIndex={2} ml={2} data-qa="cancel-btn">
        Cancel
      </Button>
    </Flex>

    <SymptomBadgeList symptoms={selectedSymptoms} onSymptomClick={onSymptomBadgeClick} />
  </SearchHeaderContainer>
);

const SymptomSearchResults: React.FC<{
  results: SearchResponse[];
  onResultClick: (result: SearchResponse) => void;
  selectedSymptoms: SearchResponse[];
}> = ({ results = [], onResultClick, selectedSymptoms }) => (
  <List
    items={results}
    keyExtractor={(symptom) => symptom.id}
    render={(symptom) => {
      const isSelected = selectedSymptoms.find((s) => s.id === symptom.id);

      return (
        <SymptomListItem
          justifyContent="space-between"
          role="button"
          tabIndex={0}
          onKeyPress={() => onResultClick(symptom)}
          onClick={() => onResultClick(symptom)}
          data-qa="symptom-search-result-list-item">
          {capitalize(symptom.label)}

          <Icon name={isSelected ? 'check-circle ' : 'plus'} color={isSelected ? 'green' : 'greyDarker'} size={24} />
        </SymptomListItem>
      );
    }}
  />
);

const SearchEmptyState = () => (
  <Flex flexDirection="column" alignItems="center">
    <CdnImage path="services-new/std.png" style={{ width: 64 }} />
    <Text mb={2} fontWeight={500} fontSize={18} mt={3} textAlign="center">
      Let’s start with the primary symptoms you are experiencing
    </Text>

    <Text color="greyDark">Examples: cough, fever, sore throat, fatigue…</Text>
  </Flex>
);

const EmptySearchResults: React.FC<{ onNewSearchClick: () => void }> = ({ onNewSearchClick }) => (
  <Flex data-qa="empty-search-result" flexDirection="column" alignItems="center">
    <Text mb={2} fontWeight={600} fontSize={18} mt={3} textAlign="center">
      Whoops!
    </Text>

    <Text color="greyDarker">We couldn&apos;t find any symptoms for that.</Text>

    <Button data-qa="start-new-search-btn" variant="grey" mt={4} onClick={onNewSearchClick}>
      Start New Search
    </Button>
  </Flex>
);

export const Response: TriageBlockConfig<never, ChiefComplaintData>['Response'] = ({
  inputValue = [],
  setCurrentInputValue,
  onSubmit,
  sessionId,
}) => {
  const [isDrawerOpen, toggleDrawer] = useToggledState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [symptoms, setSymptoms] = useState<SearchResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const toggleSymptomSelection = (item: SearchResponse) => {
    const exists = inputValue.find((s) => s.id === item.id);

    if (!exists) {
      setCurrentInputValue([...inputValue, item]);
      trackEvent('Triage Initial Symptom Added', { symptom: item.label, sessionId });
    } else {
      setCurrentInputValue(inputValue.filter((i) => i.id !== item.id));
    }
  };

  const loadOptions = useCallback(
    debounce((searchText: string, callback: (data: SearchResponse[]) => void) => {
      search({ phrase: searchText, sessionId })
        .then((data) => {
          if (!data.length) {
            onTriageEmptyResults({ sessionId, searchTerm: searchText });
          }

          callback(data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 250),
    [sessionId]
  );

  useEffect(() => {
    if (searchTerm) {
      setIsLoading(true);
      loadOptions(searchTerm, setSymptoms);
    } else {
      setSymptoms([]);
    }
  }, [searchTerm, loadOptions]);

  const searchRef = useRef<SearchInputRefAttributes>(null);

  return (
    <Box mt={3} flex={1}>
      <Metric name="Triage Add Symptoms Button Clicked" data={{ sessionId }}>
        <Button data-qa="add-symptoms-btn" block onClick={toggleDrawer}>
          Add symptoms
        </Button>
      </Metric>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={toggleDrawer}
        renderHeader={() => (
          <SymptomSearchHeader
            searchTerm={searchTerm}
            searchRef={searchRef}
            selectedSymptoms={inputValue}
            onCloseDrawer={toggleDrawer}
            onSymptomBadgeClick={toggleSymptomSelection}
            onSearchTermChange={setSearchTerm}
          />
        )}>
        <Flex flexDirection="column" minWidth={['100%', '100%', 500]} flex={1} overflow="hidden">
          <Box overflowY="auto" flex={2}>
            {searchTerm && !!symptoms.length && (
              <SymptomSearchResults
                results={symptoms}
                onResultClick={toggleSymptomSelection}
                selectedSymptoms={inputValue}
              />
            )}

            {!searchTerm && (
              <Box p={3}>
                <SearchEmptyState />
              </Box>
            )}

            {isLoading && (
              <Flex p={3} justifyContent="center" alignItems="center">
                <Spinner />
              </Flex>
            )}

            {!!searchTerm && !symptoms.length && !isLoading && (
              <Box p={3}>
                <EmptySearchResults onNewSearchClick={searchRef.current!.clear} />
              </Box>
            )}
          </Box>

          {!!inputValue?.length && (
            <Metric name="Triage Added All Symptoms Button Clicked" data={{ sessionId }}>
              <ButtonContainer>
                <Button data-qa="done-btn" block variant="primary" onClick={onSubmit} disabled={!inputValue?.length}>
                  Done
                </Button>
              </ButtonContainer>
            </Metric>
          )}
        </Flex>
      </Drawer>
    </Box>
  );
};

export const onSubmit: TriageBlockConfig<never, ChiefComplaintData>['onSubmit'] = async ({
  blockCode,
  inputValue,
  setDataValue,
  addBlock,
  dataValues,
  sessionId,
}) => {
  const evidence = inputValue.map(
    (mention) =>
      ({
        id: mention.id,
        choiceId: 'present',
        source: 'initial',
      } as const)
  );

  setDataValue('evidence', {
    [blockCode]: evidence,
  });

  const body = createDiagnosisPayload({
    ...dataValues,
    evidence: { chiefComplaint: evidence },
  });

  const data = await suggest({ ...body, sessionId });

  addBlock('suggestSymptoms', { suggestions: data });
};

export const SavedResponse: React.FC<TriageBlockChildProps<never, ChiefComplaintData>> = ({ inputValue, ...props }) => (
  <SavedResponseWrapper {...props}>
    <Text>{inputValue.map((iv) => iv.label).join(', ')}</Text>
  </SavedResponseWrapper>
);
